import React, { useEffect, useMemo, useState } from 'react';
import { StreamingImageTreeNodeData } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { hydrateRichTextVariables, renderRichTextToPlainString, normalizeStreamingRichText } from '../../richtext/ast';
import { cn } from '../../utils/cn';
import { resolveHref } from './resolveHref';
import defaultImage from './images/default-image.png';

/** Resolved intrinsic image sizes. The canvas iframe discovers these pre-paint so <img>
 * can emit width/height attributes and the browser reserves exact layout space via
 * aspect-ratio instead of shifting content when the bytes arrive. */
export type ImageDimensions = { width: number; height: number };

export interface FlexibleImageSrcOptions {
  overrideVariables?: Partial<Record<string, string>>;
  staticAssetsBaseURL?: string | null;
}

/** The one place flexible-page image sources are resolved: RichText hydration, variable
 * substitution, and static-asset base prepending. Canvas dimension discovery and the
 * <img> renderer must agree on the exact URL or dimension lookups silently miss. */
export function resolveFlexibleImageSrc(
  src: unknown,
  { overrideVariables, staticAssetsBaseURL }: FlexibleImageSrcOptions = {},
): string {
  // Callers hold schema-typed srcs as `unknown` after runtime-narrowed walks; normalize
  // handles the same union the schema produces.
  let value = normalizeStreamingRichText(src as StreamingImageTreeNodeData['src']);
  if (typeof value !== 'string') value = hydrateRichTextVariables(value, overrideVariables || {});
  if (typeof value !== 'string') value = renderRichTextToPlainString(value);
  if (staticAssetsBaseURL && value && !value.startsWith('http')) return `https://${staticAssetsBaseURL}${value}`;
  return value;
}

const PLACEHOLD_HOST = 'placehold.co';
const PLACEHOLD_QUERY_PARAM = 'mutinyPlaceholder';
const PLACEHOLD_HEX_COLOR_PATTERN = /^[0-9a-f]{3}([0-9a-f]{3})?$/i;
const PLACEHOLD_BORDER_WIDTH = 2;

const getPlaceholdBorderStyle = (src: string): React.CSSProperties | undefined => {
  try {
    const url = new URL(src);
    if (!url.searchParams.has(PLACEHOLD_QUERY_PARAM)) return undefined;
    if (url.hostname !== PLACEHOLD_HOST) return undefined;

    const segments = url.pathname.split('/').filter(Boolean);
    const fgColor = segments[2];

    if (!fgColor || fgColor.toLowerCase() === 'transparent') return undefined;

    const normalizedColor = fgColor.toLowerCase();
    const borderColor = PLACEHOLD_HEX_COLOR_PATTERN.test(normalizedColor) ? `#${normalizedColor}` : normalizedColor;

    return {
      border: `${PLACEHOLD_BORDER_WIDTH}px solid ${borderColor}`,
    };
  } catch {
    return undefined;
  }
};

type ImageProps = StreamingImageTreeNodeData & {
  id: string;
  editorClassName?: string;
  overrideVariables?: Partial<Record<string, string>>;
  staticAssetsBaseURL?: string | null;
  isEditor?: boolean;
  style?: React.CSSProperties;
  /** Canvas-only: known intrinsic sizes keyed by resolved src; absent in other renderers. */
  imageDimensions?: ReadonlyMap<string, ImageDimensions> | null;
};

export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      id,
      className,
      editorClassName,
      src,
      alt,
      overrideVariables,
      imageHref: href,
      openInNewTab,
      staticAssetsBaseURL,
      isEditor = false,
      style: styleProp,
      imageDimensions,
      ...props
    },
    ref,
  ) => {
    const [logoLoadFailed, setLogoLoadFailed] = useState(false);
    const [srcLoadFailed, setSrcLoadFailed] = useState(false);

    const resolvedSrc = useMemo(
      () => resolveFlexibleImageSrc(src, { overrideVariables, staticAssetsBaseURL }),
      [src, overrideVariables, staticAssetsBaseURL],
    );

    const resolvedHref = useMemo(() => resolveHref(href, overrideVariables), [href, overrideVariables]);

    const isLogoAsset = typeof resolvedSrc === 'string' && /(?:^|[./_-])logo(?:$|[./_-])/i.test(resolvedSrc);

    useEffect(() => {
      setLogoLoadFailed(false);
      setSrcLoadFailed(false);
    }, [resolvedSrc]);

    const onError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (isLogoAsset) {
        setLogoLoadFailed(true);
        return;
      }
      // The swap below replaces the element's source outside React; drop the failed
      // source's discovered dimensions so the fallback reserves its own intrinsic ratio.
      setSrcLoadFailed(true);
      const eventTarget = event.target as HTMLImageElement;
      eventTarget.src = defaultImage;
      eventTarget.onerror = null;
    };

    const finalSrc = resolvedSrc || defaultImage;

    const discoveredDimensions = srcLoadFailed ? undefined : imageDimensions?.get(resolvedSrc);

    const imgStyle = useMemo(() => {
      if (!resolvedSrc) return styleProp;
      const placeholderStyle = getPlaceholdBorderStyle(resolvedSrc);
      if (!placeholderStyle) return styleProp;
      return {
        ...styleProp,
        ...placeholderStyle,
      };
    }, [resolvedSrc, styleProp]);

    const imgClassName = cn('max-w-full', className, editorClassName);

    if (logoLoadFailed) {
      const wordmarkClassName = cn(
        'inline-flex max-w-full items-center font-semibold tracking-tight',
        className
          ?.split(/\s+/)
          .filter((token) => !/^(?:object-|brightness-|invert$|grayscale$)/.test(token))
          .join(' '),
        editorClassName,
      );
      const wordmark = (
        <span id={id} data-image-logo-fallback="" aria-label={alt} className={wordmarkClassName} style={styleProp}>
          {alt}
        </span>
      );

      if (resolvedHref) {
        return (
          <a href={resolvedHref} target={openInNewTab ? '_blank' : '_self'} rel="noreferrer" className="contents">
            {wordmark}
          </a>
        );
      }
      return wordmark;
    }

    if (resolvedHref) {
      return (
        <a href={resolvedHref} target={openInNewTab ? '_blank' : '_self'} rel="noreferrer" className="contents">
          <img
            id={id}
            ref={ref}
            src={finalSrc}
            alt={alt}
            className={imgClassName}
            onError={onError}
            {...props}
            width={discoveredDimensions?.width}
            height={discoveredDimensions?.height}
            style={imgStyle}
          />
        </a>
      );
    }

    return (
      <img
        id={id}
        ref={ref}
        src={finalSrc}
        alt={alt}
        className={imgClassName}
        onError={onError}
        {...props}
        width={discoveredDimensions?.width}
        height={discoveredDimensions?.height}
        style={imgStyle}
      />
    );
  },
);

Image.displayName = 'Image';
